import { Sparkles, Trophy, Medal, TrendingUp } from "lucide-react";
import { formatMoney } from "@/lib/format";
import { ordinal } from "@/lib/timeAgo";

/**
 * Where a student stands in their classroom for donations, and what it
 * would take to move up. Numbers only — no other student's name
 * (BUSINESS_RULES §9).
 */
export default function RankingCard({ ranking, firstName, classroomName }) {
  const { rank, total_students, gap_to_next_cents, gap_to_first_cents, tied_for_first, is_first, leaders_count, own_cents } = ranking;
  const noActivity = own_cents === 0 && gap_to_first_cents === 0;
  const where = `in ${classroomName} for donations`;

  let Icon = TrendingUp;
  let headline;
  let detail = null;

  if (noActivity) {
    Icon = Sparkles;
    headline = `Be ${firstName}'s first donor`;
    detail = `No one in ${classroomName} has a donation yet, so yours would put ${firstName} first.`;
  } else if (is_first && !tied_for_first) {
    Icon = Trophy;
    headline = `${firstName} is 1st ${where}`;
    detail = `Out of ${total_students} readers.`;
  } else if (tied_for_first) {
    Icon = Medal;
    headline = `${firstName} is tied for 1st ${where}`;
    detail = `Level with ${leaders_count - 1} other reader${leaders_count - 1 === 1 ? "" : "s"}. Any donation breaks the tie.`;
  } else if (rank === 2) {
    Icon = Medal;
    headline = `${firstName} is 2nd ${where}`;
    if (gap_to_next_cents != null) detail = `${formatMoney(gap_to_next_cents)} more puts ${firstName} in first.`;
  } else {
    headline = `${firstName} is ${ordinal(rank)} of ${total_students} ${where}`;
    if (gap_to_next_cents != null) {
      detail = `${formatMoney(gap_to_next_cents)} more moves ${firstName} up a spot`;
      if (gap_to_first_cents > gap_to_next_cents) detail += ` · ${formatMoney(gap_to_first_cents)} to reach first`;
      detail += ".";
    }
  }

  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-card px-5 py-4">
      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{headline}</p>
        {detail && <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</p>}
      </div>
    </div>
  );
}
